import { Component } from '@theme/component';
import { onAnimationEnd } from '@theme/utilities';
import { ThemeEvents, CartUpdateEvent } from '@theme/events';

/**
 * A custom element that displays a cart icon.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} cartBubble - The cart bubble element.
 * @property {HTMLElement} cartBubbleText - The cart bubble text element.
 * @property {HTMLElement} cartBubbleCount - The cart bubble count element.
 *
 * @extends {Component<Refs>}
 */
class CartIcon extends Component {
  requiredRefs = ['cartBubble', 'cartBubbleText', 'cartBubbleCount'];

  /** @type {number} */
  get currentCartCount() {
    return parseInt(this.refs.cartBubbleCount.textContent ?? '0', 10) || 0;
  }

  set currentCartCount(value) {
    this.refs.cartBubbleCount.textContent = value < 100 ? String(value) : '';
  }

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.onCartUpdate);
    this.ensureCartBubbleIsCorrect();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.onCartUpdate);
  }

  /**
   * Handles the cart update event.
   *
   * A producer that omits `itemCount` used to arrive here as `?? 0`, which HID the badge over
   * a full basket and cached the zero in sessionStorage. That was #60 on the free-gift path,
   * and #150 again on sections/ugc-immersive.liquid. Ignoring a non-numeric count instead
   * makes "omit the count rather than send a guess" the correct fallback for every producer,
   * which is what snippets/cart-display-count.liquid now instructs them to do.
   *
   * THE COST IS REAL AND DELIBERATE: a producer that omits the count now fails SILENTLY, as a
   * badge that does not move, rather than visibly as a zeroed one. That is the better outcome
   * for a customer and the worse one for whoever debugs the next producer.
   *
   * The NaN test is defensive, not a fix for a live bug. NaN passes both `?? 0` and
   * `typeof === 'number'`, so it would reach renderCartBubble — but nothing currently
   * produces it. assets/product-form.js computes
   * `Number(formData.get('quantity')) || Number(this.dataset.quantityDefault)`, which could
   * be NaN if neither operand existed; both render sites always supply a number
   * (blocks/buy-buttons.liquid:267 always emits data-quantity-default, and
   * snippets/quick-add.liquid:108-109 always emits a quantity input with a value).
   *
   * And the symptom would not be a badge reading "NaN". The setter above is
   * `value < 100 ? String(value) : ''`, and `NaN < 100` is false, so it would BLANK the
   * badge — after which the getter reads '' and `parseInt('') || 0` gives 0, caching "0" in
   * sessionStorage. That is the same failure as the omission case described above, which is
   * the reason to guard it at all.
   * @param {CartUpdateEvent} event - The cart update event.
   */
  onCartUpdate = async (event) => {
    const itemCount = event.detail.data?.itemCount;
    if (typeof itemCount !== 'number' || Number.isNaN(itemCount)) return;

    const comingFromProductForm = event.detail.data?.source === 'product-form-component';

    this.renderCartBubble(itemCount, comingFromProductForm);
  };

  /**
   * Renders the cart bubble.
   * @param {number} itemCount - The number of items in the cart.
   * @param {boolean} comingFromProductForm - Whether the cart update is coming from the product form.
   */
  renderCartBubble = async (itemCount, comingFromProductForm, animate = true) => {
    // If the cart update is coming from the product form, we add to the current cart count, otherwise we set the new cart count

    this.refs.cartBubbleCount.classList.toggle('hidden', itemCount === 0);
    this.refs.cartBubble.classList.toggle('visually-hidden', itemCount === 0);
    this.refs.cartBubble.classList.toggle('cart-bubble--animating', itemCount > 0 && animate);

    this.currentCartCount = comingFromProductForm ? this.currentCartCount + itemCount : itemCount;

    this.classList.toggle('header-actions__cart-icon--has-cart', itemCount > 0);

    sessionStorage.setItem(
      'cart-count',
      JSON.stringify({
        value: String(this.currentCartCount),
        timestamp: Date.now(),
      })
    );

    if (!animate) return;
    await onAnimationEnd(this.refs.cartBubbleText);

    this.refs.cartBubble.classList.remove('cart-bubble--animating');
  };

  /**
   * Checks if the cart count is correct.
   */
  ensureCartBubbleIsCorrect = () => {
    const sessionStorageCount = sessionStorage.getItem('cart-count');
    if (sessionStorageCount === null) return;

    try {
      const { value, timestamp } = JSON.parse(sessionStorageCount);
      const visibleCount = this.refs.cartBubbleCount.textContent;

      if (value === visibleCount) return;

      if (Date.now() - timestamp < 10000) {
        const count = parseInt(value, 10);

        if (count >= 0) {
          this.renderCartBubble(count, false, false);
        }
      }
    } catch (_) {
      // no-op
    }
  };
}

if (!customElements.get('cart-icon')) {
  customElements.define('cart-icon', CartIcon);
}
